import { useEffect, useCallback, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// ---------- types ----------

interface Tab {
  label: string;
  content: React.ReactNode;
}

interface Entity {
  id: string;
  name: string;
  subtitle?: string;
}

interface DetailViewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entity: Entity;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
  tabs: Tab[];
  activeTab: number;
  onTabChange: (index: number) => void;
  hasUnsavedChanges: boolean;
}

// ---------- confirmation dialog ----------

function ConfirmDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative bg-white rounded-xl border border-grove-300 shadow-xl p-6 max-w-sm mx-4">
        <h3 className="font-display text-base font-semibold text-bark-800 mb-2">
          Unsaved Changes
        </h3>
        <p className="text-sm text-bark-500 mb-5">
          You have unsaved changes. Are you sure you want to close? Your
          changes will be lost.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-bark-600 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 transition-colors"
          >
            Keep Editing
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            Discard Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- main component ----------

export default function DetailViewPanel({
  isOpen,
  onClose,
  entity,
  onPrev,
  onNext,
  tabs,
  activeTab,
  onTabChange,
  hasUnsavedChanges,
}: DetailViewPanelProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const attemptClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const confirmClose = useCallback(() => {
    setShowConfirm(false);
    onClose();
  }, [onClose]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        attemptClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, attemptClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/20 transition-opacity duration-300"
        onClick={attemptClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="relative w-full sm:w-[70vw] sm:max-w-[1200px] h-full bg-grove-50 shadow-2xl flex flex-col transform transition-transform duration-300 ease-out translate-x-0"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-grove-300 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {/* Prev / Next arrows */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onPrev ?? undefined}
                  disabled={!onPrev}
                  className="p-1 rounded-lg text-bark-400 hover:text-bark-700 hover:bg-grove-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Previous"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={onNext ?? undefined}
                  disabled={!onNext}
                  className="p-1 rounded-lg text-bark-400 hover:text-bark-700 hover:bg-grove-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Entity name */}
              <div className="min-w-0">
                <h2 className="font-display text-lg font-semibold text-bark-800 truncate">
                  {entity.name}
                </h2>
                {entity.subtitle && (
                  <p className="text-xs text-bark-400 truncate">
                    {entity.subtitle}
                  </p>
                )}
              </div>

              {/* Unsaved indicator */}
              {hasUnsavedChanges && (
                <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-honey-100 text-honey-700 border border-honey-200">
                  Unsaved
                </span>
              )}
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={attemptClose}
              className="flex-shrink-0 p-1.5 rounded-lg text-bark-400 hover:text-bark-700 hover:bg-grove-100 transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tab bar */}
          {tabs.length > 1 && (
            <div className="flex items-center gap-1 mt-3 -mb-[17px] overflow-x-auto scrollbar-hide">
              {tabs.map((tab, i) => (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => onTabChange(i)}
                  className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                    i === activeTab
                      ? "border-forest-700 text-forest-700"
                      : "border-transparent text-bark-400 hover:text-bark-600 hover:border-grove-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tabs[activeTab]?.content}
        </div>
      </div>

      {/* Unsaved changes confirmation */}
      {showConfirm && (
        <ConfirmDialog
          onConfirm={confirmClose}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>,
    document.body
  );
}
